import React from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HomeIcon from '@mui/icons-material/Home';

/**
 * Metadata for each penalty type.
 * label     – short label shown in the Select
 * icon      – MUI icon component
 * inputLabel – label for the value field
 * adornment  – end adornment text
 * inputStep  – step for the number input
 * description – full explanation shown in the info box
 * basis      – what the value is applied to (one-liner for caption)
 */
const PENALTY_META = {
  percentage: {
    label: '% of Monthly Rent',
    icon: PercentIcon,
    inputLabel: 'Penalty Rate (%)',
    adornment: '%',
    inputStep: 0.5,
    color: '#1976d2',
    description:
      'A flat percentage of the tenant\'s contractual monthly rent is charged each ' +
      'processing cycle that rent remains unpaid. The penalty does not scale with ' +
      'how much is owed — it is always based on the base rent figure.',
    example: 'Rent KES 10,000 · Rate 5 % → Penalty KES 500',
  },
  fixed: {
    label: 'Fixed Amount (KES)',
    icon: AccountBalanceWalletIcon,
    inputLabel: 'Penalty Amount (KES)',
    adornment: 'KES',
    inputStep: 50,
    color: '#7b1fa2',
    description:
      'A fixed KES amount is charged per processing cycle regardless of the ' +
      'tenant\'s outstanding balance or rent amount. Simple and predictable for ' +
      'both parties.',
    example: 'Fixed KES 500 → Penalty KES 500 every cycle',
  },
  compound_total: {
    label: 'Compound % of Total Arrears',
    icon: TrendingUpIcon,
    inputLabel: 'Compound Rate (%)',
    adornment: '%',
    inputStep: 0.5,
    color: '#d32f2f',
    description:
      'A percentage of the tenant\'s entire outstanding balance at the processing ' +
      'date — including rent arrears, utility arrears, service fees, and any ' +
      'previously accumulated penalties. The penalty is added to the balance, so ' +
      'the base grows each cycle (compounding effect).',
    example: 'Total balance KES 25,000 · Rate 5 % → Penalty KES 1,250 (added to balance)',
  },
  compound_rent_only: {
    label: 'Compound % of Rent Arrears Only',
    icon: HomeIcon,
    inputLabel: 'Compound Rate (%)',
    adornment: '%',
    inputStep: 0.5,
    color: '#e65100',
    description:
      'A percentage of the rent arrears portion of the outstanding balance only. ' +
      'Utility charges, service fees, and other non-rent items are excluded from the ' +
      'calculation base. Compounds each cycle on the rent sub-total only.',
    example:
      'Rent arrears KES 18,000 · Utilities KES 4,000 (excluded) · Rate 5 % → Penalty KES 900',
  },
};

/**
 * PenaltySettings
 *
 * A self-contained child component for the penalty rule section of
 * PaymentScheduleSettings. Renders a type selector, a value input, and a
 * contextual info card that explains the active penalty strategy.
 *
 * Props
 * ──────
 * penaltyType    string   – current penalty_type value
 * penaltyValue   string   – current penalty_value value
 * isEditing      bool     – whether the parent form is in edit mode
 * onTypeChange   fn(evt)  – onChange handler for the type Select
 * onValueChange  fn(evt)  – onChange handler for the value TextField
 */
const PenaltySettings = ({
  penaltyType,
  penaltyValue,
  isEditing,
  onTypeChange,
  onValueChange,
}) => {
  const meta = PENALTY_META[penaltyType] || PENALTY_META.percentage;
  const Icon = meta.icon;

  const isFixed = penaltyType === 'fixed';
  const isCompounding =
    penaltyType === 'compound_total' || penaltyType === 'compound_rent_only';

  return (
    <Grid container spacing={2}>
      {/* ── Type selector ── */}
      <Grid item xs={12} md={5}>
        <FormControl fullWidth margin="normal" disabled={!isEditing}>
          <InputLabel id="penalty-type-label">Penalty Type</InputLabel>
          <Select
            labelId="penalty-type-label"
            value={penaltyType}
            label="Penalty Type"
            onChange={onTypeChange}
            readOnly={!isEditing}
          >
            {Object.entries(PENALTY_META).map(([value, m]) => {
              const OptionIcon = m.icon;
              return (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OptionIcon sx={{ fontSize: 18, color: m.color }} />
                    {m.label}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      {/* ── Value input ── */}
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label={meta.inputLabel}
          value={penaltyValue}
          onChange={onValueChange}
          variant="outlined"
          margin="normal"
          type="number"
          disabled={!isEditing}
          inputProps={{ min: 0, step: meta.inputStep }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{meta.adornment}</InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {isFixed
            ? 'KES amount charged per processing cycle.'
            : 'Percentage applied each processing cycle.'}
        </Typography>
      </Grid>

      {/* ── Info card ── */}
      <Grid item xs={12}>
        <Alert
          severity="info"
          icon={<Icon sx={{ color: meta.color }} />}
          sx={{
            borderLeft: `4px solid ${meta.color}`,
            bgcolor: `${meta.color}0d`,   /* 5 % opacity tint */
            '& .MuiAlert-icon': { alignItems: 'flex-start', pt: 0.5 },
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ color: meta.color, mb: 0.5 }}>
              {meta.label}
              {isCompounding && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.8,
                    py: 0.1,
                    borderRadius: 1,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: meta.color,
                    color: 'white',
                    verticalAlign: 'middle',
                    letterSpacing: 0.5,
                  }}
                >
                  COMPOUNDING
                </Box>
              )}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {meta.description}
            </Typography>

            <Box
              sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.5,
                bgcolor: `${meta.color}1a`,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.78rem',
                color: meta.color,
              }}
            >
              Example: {meta.example}
            </Box>

            {isCompounding && (
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}
              >
                ⚠ Compounding penalties grow each cycle — communicate clearly
                with tenants and ensure this aligns with local tenancy
                regulations.
              </Typography>
            )}
          </Box>
        </Alert>
      </Grid>
    </Grid>
  );
};

export default PenaltySettings;